import { useState, useEffect } from "react";
import { getMedia, createMedia, deleteMedia } from "../api/MediaApi";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import CulsightPageLoader from "../components/ui/CulsightLoader";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "../components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Image as ImageIcon, Paperclip, Plus, Trash2, ExternalLink, FileText, FileSpreadsheet, FileArchive, FileType2, File } from "lucide-react";
import { toast } from "sonner";


const getFileMeta = (filename = "") => {
  const ext = filename.split(".").pop()?.toLowerCase() || "";
  if (["pdf"].includes(ext)) {
    return { Icon: FileText, color: "#e11d48", bg: "#fee2e2", label: "PDF" };
  }
  if (["doc", "docx"].includes(ext)) {
    return { Icon: FileText, color: "#2563eb", bg: "#dbeafe", label: "DOC" };
  }
  if (["xls", "xlsx", "csv"].includes(ext)) {
    return { Icon: FileSpreadsheet, color: "#16a34a", bg: "#dcfce7", label: "XLS" };
  }
  if (["zip", "rar", "7z"].includes(ext)) {
    return { Icon: FileArchive, color: "#d97706", bg: "#fef3c7", label: "ZIP" };
  }
  if (["ppt", "pptx"].includes(ext)) {
    return { Icon: FileType2, color: "#ea580c", bg: "#ffedd5", label: "PPT" };
  }
  if (["txt"].includes(ext)) {
    return { Icon: FileText, color: "#64748b", bg: "#f1f5f9", label: "TXT" };
  }
  return { Icon: File, color: "#8c3add", bg: "#f3e8ff", label: ext ? ext.toUpperCase().slice(0, 4) : "FILE" };
};

const emptyForm = (media_type = "image") => ({
  filename: "",
  media_type,
  file: null,
});

const Media = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("image"); // "image" | "attachment"
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm("image"));
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    fetchData(activeTab === "image");
  }, [activeTab]);

  const fetchData = async (isInitialLoad = false) => {
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setListLoading(true);
    }

    try {
      const res = await getMedia(activeTab);
      const raw = res.data?.data ?? [];
      setItems(Array.isArray(raw) ? raw : []);
    } catch (error) {
      console.error("Media fetch error:", error);
      toast.error("Failed to load media");
      setItems([]);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setListLoading(false);
      }
    }
  };

  const openCreate = () => {
    setFormData(emptyForm(activeTab));
    setPreview(null);
    setIsDialogOpen(true);
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    setFormData({ ...formData, file: selected });

    if (formData.media_type === "image" && selected.type.startsWith("image/")) {
      setPreview(URL.createObjectURL(selected));
    } else {
      setPreview(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    if (!formData.filename.trim()) {
      toast.error("Please enter a name");
      return;
    }
    if (!formData.file) {
      toast.error("Please select a file to upload");
      return;
    }

    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append("filename", formData.filename);
      payload.append("media_type", formData.media_type);
      payload.append("file", formData.file);

      await createMedia(payload);

      toast.success(`${formData.media_type === "image" ? "Image" : "Attachment"} added successfully`);
      setIsDialogOpen(false);
      setFormData(emptyForm(activeTab));
      setPreview(null);
      fetchData();
    } catch (error) {
      console.error("Media save error:", error);
      toast.error(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to save media"
      );
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    // Defensive check
    if (itemToDelete.deleteable === false) {
      toast.error("This media can't be deleted — it's currently in use.");
      return;
    }

    try {
      const res = await deleteMedia(itemToDelete.id);
      const data = res?.data;

      console.log("Delete API raw response:", res.status, data);

      const rawText =
        typeof data === "string" ? data : JSON.stringify(data ?? "");

      const looksLikeFailure =
        data?.success === false ||
        /fail|error|unable|could not/i.test(rawText);

      if (looksLikeFailure) {
        toast.error(
          data?.detail ||
          data?.message ||
          data?.error ||
          "Failed to delete item"
        );
      } else {
        toast.success(
          data?.detail ||
          data?.message ||
          "Deleted successfully"
        );

        setShowDeleteDialog(false);
        setItemToDelete(null);
      }

      fetchData();
    } catch (error) {
      console.error("Media delete error:", error);

      toast.error(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        (typeof error.response?.data === "string"
          ? error.response.data
          : null) ||
        error.message ||
        "Failed to delete item"
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <CulsightPageLoader />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="media-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1f1f23] tracking-tight">Media</h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage images and attachments used across campaigns and templates
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="gap-2 text-white bg-[#8c3add] hover:bg-[#7a2fc4]"
          data-testid="add-media-btn"
        >
          <Plus size={18} />
          Add {activeTab === "image" ? "Image" : "Attachment"}
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 max-w-xs">
          <TabsTrigger value="image" data-testid="tab-image" className="gap-2">
            <ImageIcon size={16} />
            Image
          </TabsTrigger>
          <TabsTrigger value="attachment" data-testid="tab-attachment" className="gap-2">
            <Paperclip size={16} />
            Attachment
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* List — card grid, 3 per row */}
      {listLoading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <CulsightPageLoader />
        </div>
      ) : items.length === 0 ? (
        <Card className="border border-gray-100 shadow-sm">
          <CardContent className="py-16 text-center text-gray-500">
            No {activeTab === "image" ? "images" : "attachments"} yet — click &ldquo;Add {activeTab === "image" ? "Image" : "Attachment"}&rdquo; to upload one.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="media-grid">
          {items.map((item) => (
            <Card
              key={item.id}
              className="border border-gray-100 shadow-sm overflow-hidden"
              data-testid={`media-card-${item.id}`}
            >
              {/* Preview */}
              <div className="aspect-video bg-gray-50 flex items-center justify-center">
                {item.media_type === "image" && item.url ? (
                  <img
                    src={item.url}
                    alt={item.filename}
                    className="w-full h-full object-cover"
                  />
                ) : item.media_type === "image" ? (
                  <ImageIcon size={32} className="text-gray-300" />
                ) : (
                  (() => {
                    const { Icon, color, bg, label } = getFileMeta(item.filename);
                    return (
                      <div className="flex flex-col items-center gap-2">
                        <div
                          className="w-14 h-14 rounded-2xl flex items-center justify-center"
                          style={{ backgroundColor: bg }}
                        >
                          <Icon size={26} style={{ color }} />
                        </div>
                        <span
                          className="text-[10px] font-semibold tracking-wide px-2 py-0.5 rounded-full"
                          style={{ color, backgroundColor: bg }}
                        >
                          {label}
                        </span>
                      </div>
                    );
                  })()
                )}
              </div>

              <CardContent className="p-4">
                <p className="font-medium text-[#1f1f23] truncate" title={item.filename}>
                  {item.filename}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <div className="text-xs text-gray-500 space-y-0.5">
                    <p>{item.size ? `${(item.size / 1024).toFixed(1)} KB` : "—"}</p>
                    <p>{item.created_at ? new Date(item.created_at).toLocaleDateString() : "—"}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {item.url && (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#8c3add] hover:text-[#7a2fc4] p-1.5 rounded-lg hover:bg-[#8c3add]/10 transition-colors inline-flex items-center"
                        data-testid={`view-media-${item.id}`}
                        title={item.media_type === "image" ? "View image" : "View attachment"}
                      >
                        <ExternalLink size={16} />
                      </a>
                    )}
                    {(() => {
                      const isDeleteable = item.deleteable !== false;

                      return (
                        <TooltipProvider>
                          <Tooltip delayDuration={0}>
                            <TooltipTrigger asChild>
                              <span className={!isDeleteable ? "cursor-not-allowed" : undefined}>
                                <button
                                  onClick={() => {
                                    if (isDeleteable) {
                                      setItemToDelete(item);
                                      setShowDeleteDialog(true);
                                    }
                                  }}
                                  disabled={!isDeleteable}
                                  className={`p-1.5 rounded-lg ${isDeleteable
                                      ? "text-red-500 hover:text-red-600 hover:bg-red-50"
                                      : "text-red-500 opacity-40 pointer-events-none"
                                    }`}
                                  data-testid={`delete-media-${item.id}`}
                                >
                                  {isDeleteable ? (
                                    <Trash2 size={16} />
                                  ) : (
                                    <span className="relative inline-flex items-center justify-center">
                                      <Trash2 size={16} />
                                      <span className="absolute w-[18px] h-[2px] bg-red-500 rotate-45 rounded-full" />
                                    </span>
                                  )}
                                </button>
                              </span>
                            </TooltipTrigger>

                            <TooltipContent>
                              {isDeleteable
                                ? "Delete"
                                : "You can't delete this media — it's currently in use"}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })()}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Media Dialog */}
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setFormData(emptyForm(activeTab));
            setPreview(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-2xl" data-testid="media-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {formData.media_type === "image" ? (
                <ImageIcon size={18} className="text-[#8c3add]" />
              ) : (
                <Paperclip size={18} className="text-[#8c3add]" />
              )}
              Add {formData.media_type === "image" ? "Image" : "Attachment"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreate} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={formData.filename}
                  onChange={(e) => setFormData({ ...formData, filename: e.target.value })}
                  placeholder="Enter media name"
                  required
                  data-testid="media-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={formData.media_type}
                  onValueChange={(v) => {
                    setFormData({ ...formData, media_type: v, file: null });
                    setPreview(null);
                  }}
                >
                  <SelectTrigger data-testid="media-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="image">🖼️ Image</SelectItem>
                    <SelectItem value="attachment">📎 Attachment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{formData.media_type === "image" ? "Upload Image" : "Upload Attachment"}</Label>
              <Input
                type="file"
                accept={formData.media_type === "image" ? "image/*" : "*/*"}
                onChange={handleFileChange}
                data-testid="media-file-input"
              />
            </div>

            {preview && (
              <img
                src={preview}
                alt="preview"
                className="max-h-40 rounded-lg border border-gray-200 object-contain"
              />
            )}
            {!preview && formData.file && formData.media_type === "attachment" && (
              <p className="text-sm text-gray-500">Selected file: {formData.file.name}</p>
            )}

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="text-white bg-[#8c3add] hover:bg-[#7a2fc4]"
                disabled={submitting}
                data-testid="media-submit-btn"
              >
                {submitting ? "Uploading..." : `Add ${formData.media_type === "image" ? "Image" : "Attachment"}`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          setShowDeleteDialog(open);
          if (!open) setItemToDelete(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>

          <div className="py-2">
            <p className="text-gray-600">
              Are you sure you want to delete <strong>{itemToDelete?.filename}</strong>?
            </p>
            <p className="text-sm text-gray-400 mt-2">This action cannot be undone.</p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setItemToDelete(null);
              }}
            >
              Cancel
            </Button>
            {itemToDelete?.deleteable !== false && (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
              >
                <Trash2 size={16} className="mr-2" />
                Delete
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Media;